import { createContext, useContext, useEffect, useState } from 'react';
import axios from 'axios';

const V2AuthContext = createContext(null);
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_V2 = `${BACKEND_URL}/api/v2`;

export function useV2Auth() {
  const ctx = useContext(V2AuthContext);
  if (!ctx) {
    throw new Error('useV2Auth must be used within V2AuthProvider');
  }
  return ctx;
}

export function V2AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [authLoading, setAuthLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let isMounted = true;
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setInitialLoading(false);
      return;
    }
    async function fetchMe() {
      try {
        const res = await axios.get(`${API_V2}/auth/me`, { withCredentials: true });
        if (isMounted) {
          setUser(res.data);
        }
      } catch {
        if (isMounted) {
          setUser(null);
        }
      } finally {
        if (isMounted) {
          setInitialLoading(false);
        }
      }
    }
    fetchMe();
    return () => {
      isMounted = false;
    };
  }, []);

  const refresh = async () => {
    try {
      const res = await axios.get(`${API_V2}/auth/me`, { withCredentials: true });
      setUser(res.data);
    } catch {
      setUser(null);
    }
  };

  const logout = async () => {
    setAuthLoading(true);
    setError(null);
    try {
      await axios.post(`${API_V2}/auth/logout`, null, { withCredentials: true });
      setUser(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Logout fehlgeschlagen');
    } finally {
      setAuthLoading(false);
    }
  };

  const value = { user, initialLoading, authLoading, error, setError, setAuthLoading, setUser, refresh, logout, API_V2 };

  return <V2AuthContext.Provider value={value}>{children}</V2AuthContext.Provider>;
}
